import { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Banner, EmptyState, useToast } from '@immo/ui-kit';
import type { LandIqBoardColumn, LandIqBoardTicket, ListLandIqDataRunsResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { NetworkError } from '@/lib/errors/network-error';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, MapIcon } from '@/components/layout/icons';
import { BreadcrumbLeading, ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { isLandIqPageErrorCode, LandIqErrorState } from '@/components/landiq/landiq-error-state';
import { BoardSkeleton } from '@/components/landiq/board/board-skeleton';
import { LandIqBoard } from '@/components/landiq/board/landiq-board';
import { BOARD_POLL_INTERVAL_MS, boardPollReducer, INITIAL_BOARD_POLL } from '@/lib/landiq/board-poll';
import { moveTicket } from '@/lib/landiq/move-ticket';
import {
  localRefusalToast,
  moveRefusalToast,
  moveToastText,
  type MoveToast,
} from '@/components/landiq/move-refusal-toast';

const FORBIDDEN = 403;

type ReadAt = { readonly delay: number; readonly n: number };

/** `settled` is the ticket LandIQ returned for an accepted move. The patch holds only while the board is still `base`. */
type Moving = {
  readonly to: LandIqBoardColumn;
  readonly base: ListLandIqDataRunsResponse;
  readonly settled: LandIqBoardTicket | null;
};

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

/**
 * Reads the board on mount and 25 s after each answer (a timeout chain, never an interval), so a slow
 * LandIQ never stacks reads. `reread` reads now; every move calls it.
 */
function useBoardPoll() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(boardPollReducer, INITIAL_BOARD_POLL);
  const [readAt, setReadAt] = useState<ReadAt>({ delay: 0, n: 0 });

  useEffect(() => {
    let current = true;

    const read = async () => {
      let result: Awaited<ReturnType<typeof apiClient.adminLandiqDataRuns.list>>;

      try {
        result = await apiClient.adminLandiqDataRuns.list();
      } catch (cause) {
        if (current) {
          dispatch({ type: 'failed', code: undefined, ...(cause instanceof NetworkError ? { network: cause } : {}) });
          setReadAt({ delay: BOARD_POLL_INTERVAL_MS, n: readAt.n + 1 });
        }

        return;
      }

      if (!current) return;

      if (!result.ok && result.status === FORBIDDEN) {
        navigate('/access-denied', { replace: true, state: { reason: 'missing-permission' } });

        return;
      }

      dispatch(
        result.ok ? { type: 'answered', board: result.body } : { type: 'failed', code: errorCodeOf(result.body) },
      );
      setReadAt({ delay: BOARD_POLL_INTERVAL_MS, n: readAt.n + 1 });
    };

    const timer = setTimeout(() => void read(), readAt.delay);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [readAt, navigate]);

  return { state, reread: () => setReadAt((previous) => ({ delay: 0, n: previous.n + 1 })) };
}

export function LandIqDataRunsPage() {
  const t = useTranslations('pages.admin.landiq-data-runs');
  const tErrors = useTranslations('errors');
  const { state, reread } = useBoardPoll();
  const toast = useToast();
  const [moving, setMoving] = useState<ReadonlyMap<string, Moving>>(new Map());

  const showToast = (moveToast: MoveToast) => toast.show(moveToast.variant, moveToastText(t, moveToast));

  const setEntry = (ticket: string, entry: Moving | null) =>
    setMoving((previous) => {
      const next = new Map(previous);

      if (entry === null) next.delete(ticket);
      else next.set(ticket, entry);

      return next;
    });

  async function handleDrop(card: LandIqBoardTicket, to: LandIqBoardColumn) {
    if (state.status !== 'loaded') return;

    if (to !== card.next) {
      showToast(localRefusalToast(card, to));

      return;
    }

    const entry: Moving = { to, base: state.board, settled: null };
    setEntry(card.ticket, entry);

    const outcome = await moveTicket(card.ticket, to);

    if (outcome.kind === 'moved') {
      setEntry(card.ticket, { ...entry, settled: outcome.ticket });
    } else if (outcome.kind === 'refused') {
      setEntry(card.ticket, null);
      showToast(
        moveRefusalToast(outcome.refusal, {
          stageEntries: card.entries.filter((e) => e.stage === to).length,
          ownColumn: card.column,
        }),
      );
    } else {
      setEntry(card.ticket, null);
      toast.show('danger', tErrors(isLandIqPageErrorCode(outcome.code) ? outcome.code : 'generic.unexpected'));
    }

    reread();
  }

  const title = <BreadcrumbLeading crumbs={[{ label: t('title') }]} icon={MapIcon} />;

  if (state.status === 'crashed') throw state.network ?? new Error('Failed to load the LandIQ data-runs board');

  if (state.status === 'loading') {
    return (
      <>
        <PageTopbar leading={title} />
        <BoardSkeleton />
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        <PageTopbar leading={title} />
        <LandIqErrorState code={state.code} />
      </>
    );
  }

  const { tickets } = state.board;
  const inFlight = new Set([...moving].filter(([, entry]) => entry.settled === null).map(([ticket]) => ticket));
  const patched = (ticket: LandIqBoardTicket): LandIqBoardTicket => {
    const entry = moving.get(ticket.ticket);

    if (entry === undefined || entry.base !== state.board) return ticket;

    return entry.settled ?? { ...ticket, column: entry.to, columnState: 'RUNNING', next: null };
  };

  const topbar = <PageTopbar leading={<ListLeading title={t('title')} count={tickets.length} icon={MapIcon} />} />;

  if (tickets.length === 0) {
    return (
      <>
        {topbar}
        <div className="flex h-full min-h-0 items-center justify-center">
          <EmptyState title={t('board.empty')} icon={<MapIcon className="h-16 w-16" aria-hidden />} />
        </div>
      </>
    );
  }

  const { failure } = state;

  return (
    <>
      {topbar}
      {failure !== null && (
        <div className="border-b border-border bg-surface px-4 py-3">
          <Banner
            variant="danger"
            icon={<AlertCircleIcon />}
            title={tErrors(failure !== undefined && isLandIqPageErrorCode(failure) ? failure : 'generic.unexpected')}
          />
        </div>
      )}
      <LandIqBoard
        tickets={tickets.map(patched)}
        runMetrics={state.board.runMetrics}
        inFlight={inFlight}
        onDrop={(card, to) => void handleDrop(card, to)}
      />
    </>
  );
}
